import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Package, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AuthPage() {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'buyer' });
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (!result.emailVerified) toast.info(t('auth.toast.verifyEmailHint'));
        toast.success(t('auth.toast.loginSuccess'));
        navigate(result.user.role === 'admin' ? '/admin/dashboard' : `/${result.user.role}`);
      } else {
        const result = await register(formData.email, formData.password, formData.name, formData.role);
        toast.success(result.message || t('auth.toast.regSuccess'));
        navigate(result.user.role === 'admin' ? '/admin/dashboard' : `/${result.user.role}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.toast.failed'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <Link to="/" className="inline-flex items-center text-[#64748B] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('nav.backHome')}
        </Link>

        <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] shadow-2xl p-8">
          <div className="flex items-center justify-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-lg flex items-center justify-center">
              <Package className="w-7 h-7 text-[#0B1120]" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-center text-white mb-2 font-['Outfit']">
            {isLogin ? t('auth.welcome') : t('auth.create')}
          </h1>
          <p className="text-center text-[#64748B] mb-8">
            {isLogin ? t('auth.login') : t('auth.join')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-[#94A3B8] font-medium text-sm mb-1.5 block">{t('auth.name')}</Label>
                <Input
                  id="name" type="text" data-testid="name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-[#0B1120] border-[#1E293B] text-white focus:border-[#F59E0B] focus:ring-[#F59E0B]/50"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-[#94A3B8] font-medium text-sm mb-1.5 block">{t('auth.email')}</Label>
              <Input
                id="email" type="email" data-testid="email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-[#0B1120] border-[#1E293B] text-white focus:border-[#F59E0B] focus:ring-[#F59E0B]/50"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#94A3B8] font-medium text-sm mb-1.5 block">{t('auth.password')}</Label>
              <Input
                id="password" type="password" data-testid="password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-[#0B1120] border-[#1E293B] text-white focus:border-[#F59E0B] focus:ring-[#F59E0B]/50"
              />
            </div>

            {!isLogin && (
              <div>
                <Label className="text-[#94A3B8] font-medium text-sm mb-3 block">{t('auth.role')}</Label>
                <RadioGroup
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  className="flex flex-col space-y-3"
                >
                  <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all ${formData.role === 'buyer' ? 'border-[#F59E0B] bg-[#F59E0B]/5' : 'border-[#1E293B] hover:border-[#F59E0B]/30'}`}>
                    <RadioGroupItem value="buyer" id="buyer" data-testid="role-buyer" />
                    <Label htmlFor="buyer" className="cursor-pointer flex-1">
                      <span className="font-semibold text-white">{t('auth.buyer')}</span>
                      <p className="text-sm text-[#64748B]">{t('auth.buyerDesc')}</p>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all ${formData.role === 'seller' ? 'border-[#F59E0B] bg-[#F59E0B]/5' : 'border-[#1E293B] hover:border-[#F59E0B]/30'}`}>
                    <RadioGroupItem value="seller" id="seller" data-testid="role-seller" />
                    <Label htmlFor="seller" className="cursor-pointer flex-1">
                      <span className="font-semibold text-white">{t('auth.seller')}</span>
                      <p className="text-sm text-[#64748B]">{t('auth.sellerDesc')}</p>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            <Button
              type="submit" data-testid="auth-submit-button" disabled={loading}
              className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] py-6 text-base font-bold rounded-md"
            >
              {loading ? t('common.loading') : isLogin ? t('auth.loginBtn') : t('auth.createBtn')}
            </Button>

            {isLogin && (
              <div className="text-center">
                <Link to="/forgot-password" className="text-sm text-[#64748B] hover:text-[#F59E0B] transition-colors" data-testid="forgot-password-link">
                  {t('auth.forgot')}
                </Link>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-[#F59E0B] hover:text-[#FCD34D] font-medium transition-colors"
            >
              {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
